/**
 * @file 云钱包赎回模块相关hook
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { UnStakeOption, UnstakeType, Process } from '@/types/unstake';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { postUnstakeFee, postUnstake } from '@/services/unstake';
import BigNumber from 'bignumber.js';
import { useFm } from '@web3/base-hooks';
import { debounce } from 'lodash-es';
import { GasFeeInfo } from '@/types/common';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { FeeLevel } from '@/types/common';
import { ethAddress } from '@/config/address';
import { useToast } from '@web3/base-hooks';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';
import { PostUnstakeFeeParams, PostUnstakeParams } from '@/types/unstake';
import { getUnstakeEstimateToken } from '@/services/unstake';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { ShowType } from '@/types/account';

/**
 * @file gas费相关hook
 */
export const useGasFee = (option: Partial<UnStakeOption>) => {
  const { unstakeAmount, selectCoinList, unstakeType, process, showType } =
    option as UnStakeOption;
  const [gasFeeInfo, setGasFeeInfo] = useState<Partial<GasFeeInfo>>({});
  const [gasFeeIsLoading, setGasFeeIsLoading] = useState(false);
  const [gasFeeLevel, setGasFeeLevel] = useState(FeeLevel.Middle);
  const { coinInfoStore } = useCoinInfoContext();
  const { poolInfo } = usePoolInfoContext();
  const selectedGasFeeInfo = gasFeeInfo?.[gasFeeLevel];
  const [loopPercent, setLoopPercent] = useState(0);
  const { toastWarn } = useToast();
  const timer = useRef<NodeJS.Timeout>();
  const uid = useRef(0);
  const gasToken = coinInfoStore?.[`${poolInfo?.chainCode}${ethAddress}`];
  const gasFeePrice = BigNumber(selectedGasFeeInfo?.gasFeeMax || 0)
    .times(gasToken?.price)
    .toString();
  const gasStore = {
    hasGasFee: !!selectedGasFeeInfo?.gasFeeMax,
    level: gasFeeLevel,
    amount: selectedGasFeeInfo?.gasFeeMax || '0',
    price: gasFeePrice,
    gasPrice: selectedGasFeeInfo?.gasPrice || '0',
    max: gasFeeInfo?.gasMax || '0',
    estimateFee: selectedGasFeeInfo?.gasFee || '0.009',
    priority: selectedGasFeeInfo?.priority || '0',
    symbol: gasToken?.symbol || '',
    isLoading: gasFeeIsLoading,
    limit: gasFeeInfo?.gasLimit || '100000',
    loopPercent: loopPercent * 20,
  };
  const { walletAddress } = useCurrentChainAddress(poolInfo?.chainCode);

  const fetchGasFee = async (
    selfUid: number,
    selfUnstakeAmount: string,
    selfUnstakeType: UnstakeType,
    selfSelectCoinList: string[]
  ) => {
    setGasFeeIsLoading(true);
    const params: PostUnstakeFeeParams = {
      poolId: poolInfo?.id,
      chainCode: poolInfo?.chainCode,
      poolCode: poolInfo?.poolCode,
      poolType: poolInfo?.poolType,
      lpTokens: BigNumber(selfUnstakeAmount || 0).toString(),
      slippage: '0.5',
      isSingle: selfUnstakeType === UnstakeType.Single,
      withdrawAddresses: selfSelectCoinList?.map((item) => ({
        contractAddress: item,
      })),
      level: FeeLevel.Middle,
      unstakeType:
        showType === ShowType.DelayUnStake ||
        showType === ShowType.ConfimDelayUnStake
          ? 1
          : 0,
    };

    if (poolInfo?.chainCode === 'SOL') {
      const { min } = poolInfo?.poolExtendDTO?.unstakeRange || {};
      // 赎回金额小于最小赎回金额时，不请求接口
      if (min && BigNumber(selfUnstakeAmount || 0).lt(min)) {
        return;
      }
      const { success, result: unstakeEstimateResult } =
        await getUnstakeEstimateToken({
          chainId: poolInfo?.chainId,
          poolId: poolInfo?.id,
          lpTokens: BigNumber(selfUnstakeAmount || 0).toString(),
          type: unstakeType,
          unstakeTokens: (unstakeType === UnstakeType.Single
            ? poolInfo?.singleCoins ?? []
            : poolInfo?.multipleCoins ?? []
          ).map((item) => item?.contractAddress),
          slippage: '',
          walletAddress,
          unstakeType:
            showType === ShowType.DelayUnStake ||
            showType === ShowType.ConfimDelayUnStake
              ? 1
              : 0,
        });
      if (success) {
        // TODO: 未来可能有多种单比选择
        const contractAddress = selfSelectCoinList[0];
        const estimate = unstakeEstimateResult?.tokenAmounts?.filter(
          (coinAmountInfo) => {
            return coinAmountInfo.contractAddress === contractAddress;
          }
        )[0];
        params.slippage = '';
        params.minOutAmount = estimate.amount;
        params.unstakeType = showType;
      }
    }

    const { success, result, message } = await postUnstakeFee(params);

    if (selfUid !== uid.current) return;

    setGasFeeIsLoading(false);

    if (!success) {
      toastWarn(message);
      return false;
    }

    setGasFeeInfo(result.gas);

    return true;
  };

  const startLoopGasFee = useCallback(
    debounce(
      async (
        percent = 0,
        selfUnstakeAmount: string,
        selfUnstakeType: UnstakeType,
        selfSelectCoinList: string[]
      ) => {
        clearTimeout(timer.current);
        if (
          BigNumber(selfUnstakeAmount).isNaN() ||
          BigNumber(selfUnstakeAmount).eq(0)
        )
          return;

        setLoopPercent(percent);

        if (percent === 0) {
          uid.current = Math.random();
          const result = await fetchGasFee(
            uid.current,
            selfUnstakeAmount,
            selfUnstakeType,
            selfSelectCoinList
          );
          // 报错则取消轮循
          if (!result) return;
        }

        timer.current = setTimeout(() => {
          if (percent === 4) {
            setLoopPercent(5);
            startLoopGasFee(
              0,
              selfUnstakeAmount,
              selfUnstakeType,
              selfSelectCoinList
            );
          } else {
            startLoopGasFee(
              percent + 1,
              selfUnstakeAmount,
              selfUnstakeType,
              selfSelectCoinList
            );
          }
        }, 2000);
      },
      500
    ),
    []
  );

  const onChangeGasFeeLevel = (gasFeeLevel: FeeLevel) => {
    setGasFeeLevel(gasFeeLevel);
  };

  // 每次更改输入框则清空gasfee
  useEffect(() => {
    setGasFeeInfo({});
  }, [unstakeAmount, selectCoinList]);

  useEffect(() => {
    if (process !== Process.None) {
      clearTimeout(timer.current);
      return;
    }
    startLoopGasFee(0, unstakeAmount, unstakeType, selectCoinList);
    return () => {
      clearTimeout(timer.current);
      uid.current = 0;
    };
  }, [selectCoinList, startLoopGasFee, unstakeAmount, unstakeType, process]);

  return {
    gasFeeInfo,
    gasStore,
    onChangeGasFeeLevel,
  };
};

/**
 * 按钮状态相关hook
 */
export const useButtonStatus = (option: Partial<UnStakeOption>) => {
  const {
    showType,
    isSolLoss,
    unstakeAmount,
    slippageIsLost,
    gasStore,
    onChangeProcess,
    onChangeShowType,
  } = option as UnStakeOption;
  const fm = useFm();
  const { poolInfo } = usePoolInfoContext();
  const textIsError = useRef(false);
  const buttonText = fm('remove_liquidity');

  const onChangeTextIsError = (v: boolean) => {
    textIsError.current = v;
  };

  const buttonDisabled = (() => {
    if (BigNumber(unstakeAmount || 0).lte(0)) {
      return true;
    }

    if (!gasStore?.hasGasFee) {
      return true;
    }

    if (textIsError.current) {
      return true;
    }
  })();

  const onClickUnstake = () => {
    scTrack(Event.Web3UserClick, {
      page_name: 'StakingWeb_bbSOLPageredeem',
      button_name:
        showType === ShowType.DelayUnStake
          ? 'Redeem_Delayed'
          : 'Redeem_Confirm',
    });

    if (isSolLoss) {
      onChangeProcess(Process.SolLoss);
      return;
    }

    if (slippageIsLost) {
      onChangeProcess(Process.Slippage);
      return;
    }

    if (poolInfo.poolExtendDTO.delayTime) {
      onChangeProcess(Process.Delay);
      return;
    }

    onChangeProcess(Process.Confirm);
    onChangeShowType(
      showType === ShowType.DelayUnStake
        ? ShowType.ConfimDelayUnStake
        : ShowType.ConfirmUnstake
    );
  };

  return {
    buttonText,
    buttonDisabled,
    onClickUnstake,
    onChangeTextIsError,
  };
};

/**
 * 赎回操作相关状态
 */
export const useUnstake = (option: Partial<UnStakeOption>) => {
  const {
    unstakeCoinAmountStore,
    unstakeAmount,
    slippage,
    gasStore,
    selectCoinList,
    showType,
    onChangeProcess,
  } = option as UnStakeOption;
  const { poolInfo } = usePoolInfoContext();
  const [confirmButtonIsLoading, setConfirmButtonIsLoading] = useState(false);
  const [orderCode, setOrderCode] = useState('');
  const { toastWarn } = useToast();
  const beginUnstake = async () => {
    setConfirmButtonIsLoading(true);

    const params: PostUnstakeParams = {
      poolId: poolInfo.id,
      chainCode: poolInfo.chainCode,
      poolCode: poolInfo.poolCode,
      poolType: poolInfo.poolType,
      // 后端必须要传滑点参数，哪怕无意义
      slippage: +slippage ? slippage : '0.5',
      estimateFee: [BigNumber(gasStore?.estimateFee).toNumber()],
      gasLimit: BigNumber(gasStore?.limit).toNumber(),
      lpTokens: BigNumber(unstakeAmount).toString(),
      withdrawAddresses: selectCoinList.map((item) => ({
        contractAddress: item,
      })),
      priority: BigNumber(gasStore?.priority).toNumber(),
      baseFee: BigNumber(gasStore?.gasPrice).toNumber(),
      gasMax: BigNumber(gasStore?.max).toNumber(),
      maxPriorityFeePerGas: BigNumber(gasStore?.priority).toNumber(),
    };
    if (poolInfo.chainCode === 'SOL') {
      params.minOutAmount = unstakeCoinAmountStore?.[selectCoinList[0]]?.amount;
      params.router = unstakeCoinAmountStore?.[selectCoinList[0]]?.router ?? '';
      params.unstakeType =
        showType === ShowType.DelayUnStake ||
        showType === ShowType.ConfimDelayUnStake
          ? 1
          : 0;
    }
    const { success, result, message } = await postUnstake(params);

    setConfirmButtonIsLoading(false);

    if (!success) {
      toastWarn(message);
      return;
    }

    if (success) {
      onChangeProcess(Process.Result);
      setOrderCode(result.orderCode);
    }
  };
  return {
    orderCode,
    confirmButtonIsLoading,
    beginUnstake,
  };
};
